'use client'

import { useActiveTab } from '@/hooks/useActiveTab'
import { useSwiperAtEnd } from '@/hooks/useSwiperAtEnd'
import { useSwiperWidth } from '@/hooks/useSwiperWidth'
import type { FC } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import type { Swiper as SwiperType } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { LinkTab, type LinkTabType } from '../atoms/LinkTab'

// Import Swiper styles
import { useScrollTo } from '@/hooks/useScrollTo'
import { cx } from 'cva'
import 'swiper/css'

export type LinkTabHeaderProps = {
	tabs: LinkTabType[]
	prefetch?: boolean
	isSticky?: boolean
	scrollOffset?: number
}

export const LinkTabHeader: FC<LinkTabHeaderProps> = ({
	tabs,
	prefetch,
	isSticky = false,
	scrollOffset = 0,
}) => {
	const swiperRef = useRef<SwiperType | null>(null)
	const activeHref = useActiveTab(tabs, scrollOffset)

	const swiperWidth = useSwiperWidth(swiperRef)
	const atEnd = useSwiperAtEnd(swiperRef)
	const showFade = isSticky && !atEnd
	const { scrollTo, isScrolling } = useScrollTo()

	const swiperId = useId()

	// Automatically slide to active tab
	useEffect(() => {
		const inst = swiperRef.current
		if (inst && !isScrolling) {
			const index = tabs.findIndex((tab) => tab.href === activeHref)
			if (index > -1) inst.slideTo(index)
		}
	}, [tabs, activeHref, isScrolling])

	// Track target tab when clicking on a tab
	const [targetIndex, setTargetIndex] = useState<number | null>(null)
	useEffect(() => {
		// Clear target when we reach it
		if (!isScrolling) setTargetIndex(null)
	}, [isScrolling])

	return (
		<>
			<style jsx global>{`
				#${swiperId} .swiper-wrapper::after {
					content: '';
					position: absolute;
					bottom: 0;
					left: 0;
					border-bottom: 1px solid var(--color-grey-20);
					width: ${swiperWidth}px;
					display: ${isSticky ? 'none' : 'block'};
					pointer-events: none;
				}
				#${swiperId}::after {
					content: '';
					position: absolute;
					z-index: 1;
					left: calc(100% - ${showFade ? '32px' : '0px'});
					bottom: 0;
					width: 100%;
					height: 100%;
					background: linear-gradient(to right, transparent, white 32px);
					opacity: ${isSticky ? 1 : 0};
					transition:
						left 0.2s,
						opacity 0.2s;
					pointer-events: none;
				}
			`}</style>
			<Swiper
				onSwiper={(swiper) => {
					swiperRef.current = swiper
				}}
				slidesPerView={'auto'}
				spaceBetween={16}
				navigation={false}
				centeredSlides={false}
				centerInsufficientSlides={false}
				loop={false}
				breakpoints={{
					768: {
						spaceBetween: 24,
					},
				}}
				id={swiperId}
				className={cx(
					'!overflow-visible relative border-b-grey-20 !mx-0 duration-200',
					isSticky ? 'w-[calc(100%-2.75rem-16px)]' : 'w-full',
				)}
				resizeObserver
			>
				{tabs.map((tab, index) => {
					const isTarget = targetIndex === index
					const isActive = activeHref === tab.href
					const slideTo = () => {
						setTargetIndex(index)
						swiperRef.current?.slideTo(index)
					}
					return (
						<SwiperSlide key={tab.label} className="!w-auto !opacity-100 relative z-1">
							<LinkTab
								key={tab.label}
								href={tab.href}
								label={tab.label}
								prefetch={prefetch}
								scrollOffset={scrollOffset}
								isActive={isScrolling ? isTarget : isActive}
								scrollFn={scrollTo}
								slideTo={slideTo}
							/>
						</SwiperSlide>
					)
				})}
			</Swiper>
		</>
	)
}

export default LinkTabHeader
